import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@eika/button';
import { InfoCard } from '@eika/card';
import { Link } from '@eika/link';
import { Loader } from '@eika/loader';
import { RichIcon, RichIconTypes } from '@eika/rich-icon';

import { FromAccountSelect } from '../../../../components/NettbankCreateTransactions/components';
import { ErrorCardWrapper } from '../../../../components/shared/ErrorCardWrapper';
import { useConfig } from '../../../../defaultConfig';
import useTranslation from '../../../../hooks/useTranslation';
import { useSigningContext } from '../../../../signingContext/signingContext';
import { useAvtaleGiroMoveContext } from '../../context/AvtaleGiroMoveContext';
import ROUTES from '../../routes';

const ChooseAvtaleGiroPaymentAccount: React.FC = () => {
    const translated = useTranslation();
    const navigate = useNavigate();
    const { avtalegiroLink, frontPageLink } = useConfig();

    const { dynamicLinkId } = useSigningContext();

    const {
        debitAccount: [debitAccount, setDebitAccount],
        error: [error, setErrorByKey],
        avtaleGiros,
        accounts,
    } = useAvtaleGiroMoveContext();

    const targetFromAccount = useMemo(() => {
        if (!debitAccount && accounts.data.length) {
            setDebitAccount(accounts.data[0].accountNumber);
            return accounts.data[0];
        }
        return accounts.data.find((account) => account.accountNumber.value === debitAccount?.value);
    }, [setDebitAccount, debitAccount, accounts.data]);

    const moveToNextStep = useCallback(
        (e: React.FormEvent) => {
            e.preventDefault();
            setErrorByKey('debitAccount', undefined);
            if (!debitAccount) {
                setErrorByKey('debitAccount', translated.moveAvtaleGiro.errors.debitAccountNotSelected);
                return;
            }
            if (!dynamicLinkId) return;
            navigate(ROUTES.chooseAgreements);
        },
        [debitAccount, dynamicLinkId, navigate, setErrorByKey, translated.moveAvtaleGiro],
    );

    useEffect(() => {
        if (!dynamicLinkId) navigate('/');
    }, [dynamicLinkId, navigate]);

    if (avtaleGiros.isLoading)
        return (
            <div className="move-avtalegiro-loading-wrapper">
                <Loader />
            </div>
        );

    if (avtaleGiros.error)
        return (
            <ErrorCardWrapper
                exception={avtaleGiros.error}
                iconType={RichIconTypes.NETTVERKSBRUDD_HARE}
                title={translated.errors.somethingWentWrong}
                defaultError={translated.moveAvtaleGiro.errors.couldNotFindExternalAvtaleGiros}
            >
                {avtalegiroLink && (
                    <Link linkType="button-positive" href={avtalegiroLink.url}>
                        {translated.goBack}
                    </Link>
                )}
            </ErrorCardWrapper>
        );

    if (!avtaleGiros.isLoading && !avtaleGiros.error && avtaleGiros.data.length === 0) {
        return (
            <InfoCard className="move-avtalegiro-form__no-avtalegiro" skipBackground>
                <RichIcon type={RichIconTypes.FANGER_SOMMERFUGLER} />
                <InfoCard.TitleH2>{translated.moveAvtaleGiro.foundNoAvtaleGiros}</InfoCard.TitleH2>
                <section className="move-avtalegiro__action-button-wrapper">
                    <Link href={frontPageLink?.url} linkType="button-positive">
                        {translated.moveAvtaleGiro.backToTheFrontpage}
                    </Link>
                </section>
            </InfoCard>
        );
    }

    return (
        <form className="move-avtalegiro-form" onSubmit={moveToNextStep}>
            <section className="move-avtalegiro-form__form-content">
                <div className="move-avtalegiro-form__intro-wrapper">
                    <h2 id="external-from-account-label" className="move-avtalegiro-form__choose-accounts-title">
                        {translated.chooseAccount}
                    </h2>
                    <span>{translated.moveAvtaleGiro.newHome}</span>
                </div>
                <FromAccountSelect
                    describedBy="external-from-account-label"
                    className={classNames('transaction-form__from-account-selector open')}
                    selected={debitAccount}
                    selectedAccount={targetFromAccount}
                    id="avtalegiro-from-account"
                    placeholder={translated.moveAvtaleGiro.chooseAvtaleGiroPaymentAccount}
                    items={accounts.data}
                    onSelect={setDebitAccount}
                    locked={false}
                    isLoading={accounts.isLoading}
                    isPaymentModalOpen
                    error={error.debitAccount ? { className: '', message: error.debitAccount } : undefined}
                />
            </section>
            <section className="move-avtalegiro__action-button-wrapper">
                <Button type="submit">{translated.moveAvtaleGiro.moveOn}</Button>
                {avtalegiroLink && <Link href={avtalegiroLink.url}>{translated.cancel}</Link>}
            </section>
        </form>
    );
};

export default ChooseAvtaleGiroPaymentAccount;
